import React, { useMemo, useState } from "react";
import moment from "moment";
import { message } from "antd";
import { GlobalOutlined } from "@ant-design/icons";
import xlsx from "xlsx";
import VehicleToMap from "./VehicleToMap";
import { VehicleStatusToKorean } from "../../synapse/services/job-setting.interface";
import { VehicleModelTypes } from "../../synapse/services/camp.interface";
import { VehicleBulkStockOutModal, MAX_BULK_STOCK_OUT } from "./VehicleBulkStockOutModal";
import { VehicleStatusSettingModal } from "./VehicleStatusSettingModal";
import { DynamicPricingSettingModal } from "./DynamicPricingSettingModal";
import {
  exportVehiclesV2,
  getVehiclesMapV2,
  resolveVehicleSelectionIdsV2,
  updateVehicleDynamicPriceV2,
  updateVehicleStatusV2,
  updateVehicleWorkTargetsV2,
} from "../service/scooters.service";
import {
  TaskDefinitionDTO,
  VehicleListV2MapItem,
  VehicleListV2Item,
  VehicleListV2Selection,
} from "../service/scooter.interface";
import { ControlButton, TableTitleButtonsWrapper } from "../../common/table/table.style";
import { useLanguageContext } from "../../common/language/LanguageContext";
import { useResourceContext } from "../../common/resource/useResourceContext";
import { vehicleListV2ExcelRows } from "../service/vehicle-list-v2.utils";

interface Props {
  selection: VehicleListV2Selection | null;
  selectedCount: number;
  taskDefinitions: TaskDefinitionDTO[];
  bandNames: Map<string, string>;
  belongingNames: Map<number, string>;
  canControl: boolean;
  canStockOut?: boolean;
  canDynamicPrice: boolean;
  deployedCount?: number;
  canAddTask: boolean;
  showInspect: boolean;
  onLoadLastRide: () => void;
  lastRideLoading: boolean;
  lastRideAtByVehicleId: Record<string, string | null>;
  selectedItems?: Record<string, VehicleListV2Item>;
  onSelectedItemsLoaded?: (items: VehicleListV2Item[]) => void;
  onAddTask: () => void;
  onInspect: () => void;
  onChanged: () => void;
}

export function VehicleListV2Actions(props: Props) {
  const {
    selection,
    selectedCount,
    bandNames,
    canControl,
    canStockOut = false,
    canDynamicPrice,
    deployedCount,
    canAddTask,
    showInspect,
    onLoadLastRide,
    lastRideLoading,
    lastRideAtByVehicleId,
    selectedItems = {},
    onSelectedItemsLoaded,
    onAddTask,
    onInspect,
    onChanged,
  } = props;
  const { R } = useResourceContext();
  const { languageType } = useLanguageContext();
  const [stockOutIds, setStockOutIds] = useState<string[] | null>(null);
  const [bulkVisible, setBulkVisible] = useState(false);
  const [bulkHasStatusDisabledBand, setBulkHasStatusDisabledBand] = useState(false);
  const [bulkSelection, setBulkSelection] = useState<VehicleListV2Selection | null>(null);
  const [hasW1, setHasW1] = useState(false);
  const [bulkVehicles, setBulkVehicles] = useState<Array<{ vehicleId: string; imei: string; qr: string; smodel: VehicleModelTypes }>>([]);
  const [dpSelection, setDpSelection] = useState<VehicleListV2Selection | null>(null);
  const [dpImeis, setDpImeis] = useState<string[]>([]);
  const [mapItems, setMapItems] = useState<VehicleListV2MapItem[]>([]);
  const [mapVisible, setMapVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dynamicPriceVisible, setDynamicPriceVisible] = useState(false);

  const markers = useMemo(
    () => mapItems.map((item) => ({ ...item, simei: item.shortImei })),
    [mapItems]
  );

  function requireSelection() {
    if (!selection || selectedCount === 0) {
      message.warning("기기를 선택해주세요.");
      return false;
    }
    return true;
  }

  function errorMessage(error: any) {
    return error?.response?.data?.message || error?.message || "요청을 처리하지 못했습니다.";
  }

  async function run(action: () => Promise<any>, notify = false) {
    if (loading) {
      message.warning("로딩중...");
      throw new Error("로딩중...");
    }
    try {
      setLoading(true);
      const result = await action();
      if (result?.failedCount > 0) {
        throw new Error("요청을 처리하지 못했습니다.");
      }
      if (notify) message.success("DP 설정 성공");
    } catch (error) {
      if (notify) message.error(errorMessage(error));
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function openBulk(dynamicPrice = false) {
    if (!requireSelection() || !selection || loading) return;
    let limit = MAX_BULK_STOCK_OUT;
    let limitMessage = `일괄 출고는 ${MAX_BULK_STOCK_OUT}대 이하로 선택해 주세요`;
    if (dynamicPrice) {
      limit = 30;
      limitMessage = "다이나믹 프라이싱 일괄 설정은 30대 이하로만 가능합니다.";
    } else if (canControl) {
      limit = 2000;
      limitMessage = "일괄조작은 2000대 이하로 선택해주세요.";
    }
    if (selectedCount > limit) {
      message.error(limitMessage);
      return;
    }
    try {
      setLoading(true);
      const resolved = await resolveVehicleSelectionIdsV2({ selection, includeVehicleDetails: true });
      if (!dynamicPrice && resolved.hasStatusDisabledBand && !canStockOut) {
        message.error("상태 변경이 불가한 밴드 소속 기기가 선택됐습니다. 다시 선택해주세요.");
        return;
      }
      if (resolved.vehicleIds.length === 0) {
        message.warning("기기를 선택해주세요.");
        return;
      }
      if (resolved.vehicleIds.length > limit) {
        message.error(limitMessage);
        return;
      }
      if (!resolved.vehicles || resolved.vehicles.length !== resolved.vehicleIds.length) {
        message.error("선택된 기기 정보를 불러오지 못했습니다. 다시 시도해주세요.");
        return;
      }
      if (dynamicPrice) {
        setDpSelection({ mode: "IDS", vehicleIds: [...resolved.vehicleIds] });
        setDpImeis(resolved.vehicles.map((vehicle) => vehicle.imei));
        setDynamicPriceVisible(true);
        return;
      }
      setBulkSelection({ mode: "IDS", vehicleIds: [...resolved.vehicleIds] });
      setBulkHasStatusDisabledBand(resolved.hasStatusDisabledBand);
      setBulkVehicles(resolved.vehicles.map((vehicle) => ({ vehicleId: vehicle.id, imei: vehicle.imei, qr: vehicle.shortImei, smodel: vehicle.smodel as VehicleModelTypes })));
      setHasW1(resolved.hasW1);
      setBulkVisible(true);
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  function openStockOut() {
    if (!canStockOut || loading || bulkSelection?.mode !== "IDS") return;
    const ids = [...new Set(bulkSelection.vehicleIds || [])];
    const limitMessage = `일괄 출고는 ${MAX_BULK_STOCK_OUT}대 이하로 선택해 주세요`;
    if (ids.length > MAX_BULK_STOCK_OUT) {
      message.error(limitMessage);
      return;
    }
    if (!ids.length) {
      message.warning("출고할 기기를 선택해 주세요");
      return;
    }
    setBulkVisible(false);
    setStockOutIds(ids);
  }

  async function downloadExcel() {
    if (!requireSelection() || !selection) return;
    try {
      setLoading(true);
      const { items } = await exportVehiclesV2({ selection });
      const snapshots = items.map((item) => selectedItems[item.id] || { ...item,
        allocatedDays: item.allocatedAt ? moment().utc().diff(moment(item.allocatedAt), "days") : "",
      });
      onSelectedItemsLoaded?.(snapshots);
      const byId = new Map(snapshots.map((item) => [item.id, item]));
      const orderedIds = selection.mode === "IDS" ? selection.vehicleIds || [] : items.map((item) => item.id);
      const selectedRows = orderedIds.map((id) => selectedItems[id] || byId.get(id)).filter((item): item is VehicleListV2Item => !!item);
      const rows = vehicleListV2ExcelRows(selectedRows, languageType, bandNames, lastRideAtByVehicleId);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, xlsx.utils.json_to_sheet(rows), "Sheet1");
      xlsx.writeFile(workbook, "기기 리스트.xlsx");
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function openMap() {
    if (!selection || selectedCount === 0) {
      setMapItems([]);
      setMapVisible(true);
      return;
    }
    try {
      setLoading(true);
      const response = await getVehiclesMapV2({ selection });
      const byId = new Map(response.items.map((item) => [item.id, item]));
      const orderedIds = selection.mode === "IDS" ? selection.vehicleIds || [] : response.items.map((item) => item.id);
      setMapItems(orderedIds.map((id): VehicleListV2MapItem | undefined => {
        const item = selectedItems[id] || byId.get(id);
        return item ? { ...item, lat: item.lat ?? 0, lng: item.lng ?? 0 } : undefined;
      }).filter((item): item is VehicleListV2MapItem => !!item));
      setMapVisible(true);
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <TableTitleButtonsWrapper>
        <div className="selection-text">{selectedCount} {R.text.selected_items}</div>
        <ControlButton type="primary" onClick={() => deployedCount == null
          ? message.warning("기기 현황을 불러오는 중입니다.")
          : window.alert(`${deployedCount}대 만큼 배치되어있습니다.`)}>
          {R.text.check_number_of_deployed_vehicles}
        </ControlButton>
        <ControlButton type="primary" danger loading={lastRideLoading} onClick={onLoadLastRide}>
          {R.text.load_last_ride_date}
        </ControlButton>
        {selectedCount > 0 && (canControl || canStockOut) && (
          <ControlButton type="primary" danger loading={loading} onClick={() => openBulk()}>
            일괄 조작
          </ControlButton>
        )}
        {canAddTask && (
          <ControlButton ghost type="primary" danger onClick={onAddTask}>
            작업정의 추가
          </ControlButton>
        )}
        {selectedCount > 0 && <ControlButton loading={loading} onClick={downloadExcel}>{R.text.export}</ControlButton>}
        {showInspect && (
          <ControlButton type="primary" ghost onClick={onInspect}>
            {R.text.full_inspection}
          </ControlButton>
        )}
        <ControlButton icon={<GlobalOutlined />} loading={loading} onClick={openMap}>
          TO MAP
        </ControlButton>
        {selectedCount > 0 && canDynamicPrice && (
          <ControlButton type="primary" danger loading={loading} onClick={() => openBulk(true)}>
            일괄 DP 설정
          </ControlButton>
        )}
      </TableTitleButtonsWrapper>

      {stockOutIds && <VehicleBulkStockOutModal
        vehicleIds={stockOutIds} onClose={() => setStockOutIds(null)} onChanged={onChanged}
      />}

      {bulkVisible && bulkSelection && <VehicleStatusSettingModal
        visible={bulkVisible}
        close={() => setBulkVisible(false)}
        fetch={onChanged}
        vehicles={bulkVehicles}
        includesW1={hasW1}
        canControl={canControl && !bulkHasStatusDisabledBand}
        onStockOut={canStockOut ? openStockOut : undefined}
        onSetVehicleStatus={(type) => run(() => updateVehicleStatusV2({ selection: bulkSelection, type, title: VehicleStatusToKorean[type] }))}
        onSetTaskStatus={(workDefinitionId) => run(() => updateVehicleWorkTargetsV2({ selection: bulkSelection, operation: "SET", workDefinitionId }))}
        onResetTaskStatus={() => run(() => updateVehicleWorkTargetsV2({ selection: bulkSelection, operation: "CLEAR" }))}
      />}

      {mapVisible && <VehicleToMap viewType="map" changeViewType={() => setMapVisible(false)} selectedRows={markers} />}

      {dynamicPriceVisible && dpSelection && <DynamicPricingSettingModal
        visible={dynamicPriceVisible}
        close={() => setDynamicPriceVisible(false)}
        fetch={onChanged}
        target={dpImeis}
        onSetDynamicPrice={(ratio, activeHours) => run(() => updateVehicleDynamicPriceV2({ selection: dpSelection, ratio, activeHours }), true)}
      />}
    </>
  );
}
